import React from 'react';
import { UnifiedFilter } from '../generic/FilterSection';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { getDateRangeForPreset } from './dateFilterHelpers';

export interface QuickFilter {
  id: string;
  label: string;
  icon?: string | React.ReactNode;
  description?: string;
  filters: Record<string, UnifiedFilter>;
  badge?: string | number; // Optional badge to show on the button
  variant?: 'default' | 'primary' | 'secondary' | 'outline';
}

interface QuickFilterButtonsProps {
  quickFilters?: QuickFilter[];
  currentFilters: Record<string, UnifiedFilter>;
  onApplyFilters: (filters: Record<string, UnifiedFilter>) => void;
  /** Called when a quick filter is selected, with its id ('all' when cleared). Use to persist in URL. */
  onQuickFilterSelect?: (quickFilterId: string) => void;
  className?: string;
  layout?: 'horizontal' | 'vertical' | 'pills';
}

const QuickFilterButtons: React.FC<QuickFilterButtonsProps> = ({
  quickFilters = [],
  currentFilters,
  onApplyFilters,
  onQuickFilterSelect,
  className = '',
  layout = 'horizontal',
}) => {
  const { colors } = useTheme();

  if (!quickFilters || quickFilters.length === 0) {
    return null;
  }

  // Normalize filters for comparison (convert date presets to actual dates)
  // This ensures that filters with presets match filters with calculated from/to dates
  const normalizeFilters = (filters: Record<string, UnifiedFilter>): Record<string, UnifiedFilter> => {
    const normalized: Record<string, UnifiedFilter> = {};
    
    // Sort keys to ensure consistent comparison
    const sortedKeys = Object.keys(filters).sort();
    
    sortedKeys.forEach((key) => {
      const filter = filters[key];
      if (!filter || !filter.type) return;
      
      if (filter.type === 'dateRange') {
        // If it has a preset (and not 'all' or 'custom'), convert to from/to dates
        if (filter.preset && filter.preset !== 'all' && filter.preset !== 'custom') {
          const range = getDateRangeForPreset(filter.preset);
          normalized[key] = {
            type: 'dateRange',
            from: range.start ? range.start.toISOString().split('T')[0] : undefined,
            to: range.end ? range.end.toISOString().split('T')[0] : undefined,
          };
        } else if (filter.from || filter.to) {
          // If it already has from/to dates, use them (remove preset if present)
          normalized[key] = {
            type: 'dateRange',
            from: filter.from || undefined,
            to: filter.to || undefined,
          };
        }
        // Empty date ranges are not included in normalized filters
      } else if (filter.type === 'multiSelect') {
        // Sort values for consistent comparison
        if (filter.values && filter.values.length > 0) {
          normalized[key] = {
            type: 'multiSelect',
            values: [...filter.values].sort(),
          };
        }
      } else if (filter.type === 'numericRange') {
        if (filter.min !== undefined || filter.max !== undefined) {
          normalized[key] = {
            type: 'numericRange',
            min: filter.min,
            max: filter.max,
          };
        }
      } else if (filter.type === 'boolean') {
        if (filter.value !== undefined) {
          normalized[key] = {
            type: 'boolean',
            value: filter.value,
          };
        }
      } else if (filter.type === 'text' || filter.type === 'select') {
        if (filter.textValue && filter.textValue.trim() !== '') {
          normalized[key] = {
            type: filter.type,
            textValue: filter.textValue.trim(),
          };
        }
      }
    });
    
    return normalized;
  };

  // Check if current filters match a quick filter
  const isQuickFilterActive = (quickFilter: QuickFilter): boolean => {
    // Handle "All" filter specially - it's active when there are no filters
    if (quickFilter.id === 'all') {
      const hasActiveFilters = Object.keys(currentFilters).length > 0 && 
        Object.values(currentFilters).some(filter => {
          if (!filter || !filter.type) return false;
          if (filter.type === 'multiSelect') return filter.values && filter.values.length > 0;
          if (filter.type === 'dateRange') return filter.from || filter.to || (filter.preset && filter.preset !== 'all');
          if (filter.type === 'numericRange') return filter.min !== undefined || filter.max !== undefined;
          if (filter.type === 'boolean') return filter.value !== undefined;
          if (filter.type === 'text' || filter.type === 'select') return filter.textValue && filter.textValue.trim() !== '';
          return false;
        });
      return !hasActiveFilters;
    }
    
    // Normalize both filters before comparison
    const normalizedQuickFilter = normalizeFilters(quickFilter.filters);
    const normalizedCurrentFilters = normalizeFilters(currentFilters);
    
    // Compare normalized filters
    const quickFilterStr = JSON.stringify(normalizedQuickFilter);
    const currentFiltersStr = JSON.stringify(normalizedCurrentFilters);
    return quickFilterStr === currentFiltersStr;
  };


  const handleQuickFilterClick = (quickFilter: QuickFilter) => {
    // Always replace all filters with the clicked filter's filters
    // Start with an empty object to ensure we replace all filters, not merge
    const processedFilters: Record<string, UnifiedFilter> = {};
    
    // Only process filters that exist in this specific quick filter
    if (quickFilter.filters && typeof quickFilter.filters === 'object' && Object.keys(quickFilter.filters).length > 0) {
      Object.entries(quickFilter.filters).forEach(([key, filter]) => {
        if (filter && filter.type) {
          if (filter.type === 'dateRange' && filter.preset && filter.preset !== 'all' && filter.preset !== 'custom') {
            // Calculate date range for preset
            const range = getDateRangeForPreset(filter.preset);
            processedFilters[key] = {
              type: 'dateRange',
              preset: filter.preset,
              from: range.start ? range.start.toISOString().split('T')[0] : undefined,
              to: range.end ? range.end.toISOString().split('T')[0] : undefined,
            };
          } else {
            // Create a clean copy of the filter to avoid reference issues
            const cleanFilter: UnifiedFilter = { type: filter.type };
            if (filter.type === 'multiSelect' && filter.values) {
              cleanFilter.values = [...filter.values];
            } else if (filter.type === 'numericRange') {
              if (filter.min !== undefined) cleanFilter.min = filter.min;
              if (filter.max !== undefined) cleanFilter.max = filter.max;
            } else if (filter.type === 'boolean' && filter.value !== undefined) {
              cleanFilter.value = filter.value;
            } else if ((filter.type === 'text' || filter.type === 'select') && filter.textValue) {
              cleanFilter.textValue = filter.textValue;
            } else if (filter.type === 'dateRange') {
              if (filter.preset) cleanFilter.preset = filter.preset;
              if (filter.from) cleanFilter.from = filter.from;
              if (filter.to) cleanFilter.to = filter.to;
            }
            processedFilters[key] = cleanFilter;
          }
        }
      });
    }
    
    // Check if clicking the same filter that's already active
    const isCurrentlyActive = isQuickFilterActive(quickFilter);

    if (isCurrentlyActive && quickFilter.id !== 'all') {
      // If already active (and not "All"), clear filters
      onApplyFilters({});
      onQuickFilterSelect?.('all');
    } else {
      // Apply the processed quick filter (this replaces all existing filters)
      onApplyFilters(processedFilters);
      onQuickFilterSelect?.(quickFilter.id);
    }
  };

  if (layout === 'pills') {
    return (
      <div className={cn('flex flex-wrap gap-2', className)}>
        {quickFilters.map((quickFilter) => {
          const isActive = isQuickFilterActive(quickFilter);
          const variant = quickFilter.variant || 'default';

          return (
            <button
              key={quickFilter.id}
              onClick={() => handleQuickFilterClick(quickFilter)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                'hover:scale-105 active:scale-95 shadow-sm hover:shadow-md',
                isActive
                  ? variant === 'primary'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : variant === 'secondary'
                    ? 'bg-gray-600 text-white shadow-md'
                    : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200 border-2 border-indigo-500 shadow-md'
                  : variant === 'primary'
                  ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-700'
                  : variant === 'secondary'
                  ? `${colors.surfaceHover} ${colors.textSecondary} hover:${colors.surfaceHover} border ${colors.border}`
                  : `${colors.surface} ${colors.textSecondary} hover:${colors.surfaceHover} border ${colors.border}`
              )}
              title={quickFilter.description || quickFilter.label}
            >
              {quickFilter.icon && (
                <span className="text-base">
                  {typeof quickFilter.icon === 'string' ? quickFilter.icon : quickFilter.icon}
                </span>
              )}
              <span>{quickFilter.label}</span>
              {quickFilter.badge && (
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full',
                    isActive
                      ? 'bg-white/20 text-white'
                      : variant === 'primary'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                  )}
                >
                  {quickFilter.badge}
                </span>
              )}
              {isActive && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (layout === 'vertical') {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        {quickFilters.map((quickFilter) => {
          const isActive = isQuickFilterActive(quickFilter);

          return (
            <button
              key={quickFilter.id}
              onClick={() => handleQuickFilterClick(quickFilter)}
              className={cn(
                'flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                'hover:shadow-md',
                isActive
                  ? 'bg-indigo-600 text-white shadow-md'
                  : `${colors.surface} ${colors.textSecondary} hover:${colors.surfaceHover} border ${colors.border}`
              )}
              title={quickFilter.description || quickFilter.label}
            >
              <div className="flex items-center gap-2">
                {quickFilter.icon && (
                  <span className="text-base">
                    {typeof quickFilter.icon === 'string' ? quickFilter.icon : quickFilter.icon}
                  </span>
                )}
                <span>{quickFilter.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {quickFilter.badge && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200">
                    {quickFilter.badge}
                  </span>
                )}
                {isActive && (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  // Default: horizontal tabs
  return (
    <div className={cn('flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg', className)}>
      {quickFilters.map((quickFilter) => {
        const isActive = isQuickFilterActive(quickFilter);

        return (
          <button
            key={quickFilter.id}
            onClick={() => handleQuickFilterClick(quickFilter)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
              isActive
                ? 'bg-indigo-600 text-white shadow-sm'
                : `${colors.textSecondary} hover:${colors.surfaceHover}`
            )}
            title={quickFilter.description || quickFilter.label}
          >
            {quickFilter.icon && (
              <span className="text-sm">
                {typeof quickFilter.icon === 'string' ? quickFilter.icon : quickFilter.icon}
              </span>
            )}
            <span>{quickFilter.label}</span>
            {quickFilter.badge && (
              <span
                className={cn(
                  'inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full',
                  isActive
                    ? 'bg-white bg-opacity-20 text-white'
                    : 'bg-indigo-600 text-white'
                )}
              >
                {quickFilter.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default QuickFilterButtons;

